import React, { useState, useMemo } from "react";
import { FiCheck, FiX, FiChevronUp, FiChevronDown } from "react-icons/fi";
import { cn } from "utils/helpers";
import { CompanyLogoDisplay } from "newui/features/Company/components/CompanyLogo/CompanyLogoDisplay";
import type {
	EvaluationRevisionRequest,
	RevisionRequestStatus,
} from "../types/revision-review.type";

interface RevisionRequestsTableProps {
	isApproving: boolean;
	requests: EvaluationRevisionRequest[];
	onApprove: (request: EvaluationRevisionRequest) => void;
	onDecline: (request: EvaluationRevisionRequest) => void;
}

type SortField = "supplier" | "status";
type SortDirection = "asc" | "desc";

const TYPE_LABELS: Record<string, string> = {
	quote: "Quote",
	returnable_schedule: "Returnable Schedule",
	all: "All",
};

const APPROVAL_STATUS_LABELS: Record<RevisionRequestStatus, string> = {
	pending: "Pending",
	approved: "Approved",
	declined: "Declined",
	completed: "Approved",
};

const APPROVAL_STATUS_STYLES: Record<RevisionRequestStatus, string> = {
	pending: "bg-amber-50 text-amber-700",
	approved: "bg-green-50 text-green-700",
	declined: "bg-red-50 text-red-700",
	completed: "bg-green-50 text-green-700",
};

const getRevisionStatus = (status: RevisionRequestStatus): string | null => {
	if (status === "approved") {
		return "Pending Revision";
	}
	if (status === "completed") {
		return "Submitted";
	}
	return null;
};

export const RevisionRequestsTable: React.FC<RevisionRequestsTableProps> = ({
	requests,
	onApprove,
	onDecline,
	isApproving,
}) => {
	const [supplierFilter, setSupplierFilter] = useState<string[]>([]);
	const [statusFilter, setStatusFilter] = useState<RevisionRequestStatus[]>([]);
	const [sortField, setSortField] = useState<SortField | null>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
	const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
	const [showStatusDropdown, setShowStatusDropdown] = useState(false);

	const uniqueSuppliers = useMemo(() => {
		const suppliers = new Map<number, string>();
		requests.forEach((r) => {
			suppliers.set(r.supplier.id, r.supplier.name);
		});
		return Array.from(suppliers.entries()).sort((a, b) => a[1].localeCompare(b[1]));
	}, [requests]);

	const uniqueStatuses = useMemo(() => {
		const statuses = new Set<RevisionRequestStatus>();
		requests.forEach((r) => {
			statuses.add(r.status);
		});
		return Array.from(statuses);
	}, [requests]);

	const filteredAndSorted = useMemo(() => {
		let result = [...requests];

		if (supplierFilter.length > 0) {
			result = result.filter((r) => supplierFilter.includes(r.supplier.name));
		}

		if (statusFilter.length > 0) {
			result = result.filter((r) => statusFilter.includes(r.status));
		}

		if (sortField) {
			result.sort((a, b) => {
				let cmp = 0;
				if (sortField === "supplier") {
					cmp = a.supplier.name.localeCompare(b.supplier.name);
				} else if (sortField === "status") {
					cmp = a.status.localeCompare(b.status);
				}
				return sortDirection === "desc" ? -cmp : cmp;
			});
		}

		return result;
	}, [requests, supplierFilter, statusFilter, sortField, sortDirection]);

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setSortField(field);
			setSortDirection("asc");
		}
	};

	const toggleSupplierFilter = (name: string) => {
		setSupplierFilter((prev) => {
			if (prev.includes(name)) {
				return prev.filter((s) => s !== name);
			}
			return [...prev, name];
		});
	};

	const toggleStatusFilter = (status: RevisionRequestStatus) => {
		setStatusFilter((prev) => {
			if (prev.includes(status)) {
				return prev.filter((s) => s !== status);
			}
			return [...prev, status];
		});
	};

	const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
		if (sortField !== field) {
			return <FiChevronUp size={12} className="text-gray-300" />;
		}
		if (sortDirection === "asc") {
			return <FiChevronUp size={12} />;
		}
		return <FiChevronDown size={12} />;
	};

	const btnBase = "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors disabled:opacity-50";

	return (
		<div className="overflow-x-auto" data-testid="revision-requests-table">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
						<th className="px-4 py-3">
							<div className="relative inline-block">
								<button
									type="button"
									className="flex items-center gap-1 hover:text-gray-700"
									onClick={() => {
										handleSort("supplier");
										setShowSupplierDropdown(!showSupplierDropdown);
										setShowStatusDropdown(false);
									}}
									data-testid="supplier-column-header"
								>
									Supplier Name
									<SortIcon field="supplier" />
								</button>
								{showSupplierDropdown && (
									<div
										className="absolute left-0 top-full z-10 mt-1 w-56 rounded-lg border border-gray-200 bg-white shadow-lg"
										data-testid="supplier-filter-dropdown"
									>
										<div className="p-2 space-y-1">
											{uniqueSuppliers.map(([id, name]) => (
												<label key={id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
													<input
														type="checkbox"
														checked={supplierFilter.includes(name)}
														onChange={() => toggleSupplierFilter(name)}
														className="rounded border-gray-300 text-blue-600"
													/>
													<span className="text-gray-700">{name}</span>
												</label>
											))}
										</div>
										<div className="border-t border-gray-100 p-2">
											<button
												type="button"
												onClick={() => {
													setSupplierFilter([]);
													setShowSupplierDropdown(false);
												}}
												className="w-full rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-50"
											>
												Clear filter
											</button>
										</div>
									</div>
								)}
							</div>
						</th>
						<th className="px-4 py-3">Type</th>
						<th className="px-4 py-3">
							<div className="relative inline-block">
								<button
									type="button"
									className="flex items-center gap-1 hover:text-gray-700"
									onClick={() => {
										handleSort("status");
										setShowStatusDropdown(!showStatusDropdown);
										setShowSupplierDropdown(false);
									}}
									data-testid="status-column-header"
								>
									Approval Status
									<SortIcon field="status" />
								</button>
								{showStatusDropdown && (
									<div
										className="absolute left-0 top-full z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg"
										data-testid="status-filter-dropdown"
									>
										<div className="p-2 space-y-1">
											{uniqueStatuses.map((status) => (
												<label key={status} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
													<input
														type="checkbox"
														checked={statusFilter.includes(status)}
														onChange={() => toggleStatusFilter(status)}
														className="rounded border-gray-300 text-blue-600"
													/>
													<span className="text-gray-700">{APPROVAL_STATUS_LABELS[status]}</span>
												</label>
											))}
										</div>
										<div className="border-t border-gray-100 p-2">
											<button
												type="button"
												onClick={() => {
													setStatusFilter([]);
													setShowStatusDropdown(false);
												}}
												className="w-full rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-50"
											>
												Clear filter
											</button>
										</div>
									</div>
								)}
							</div>
						</th>
						<th className="px-4 py-3">Requested By</th>
						<th className="px-4 py-3">Approved/Declined By</th>
						<th className="px-4 py-3">Revision Status</th>
						<th className="px-4 py-3">Actions</th>
					</tr>
				</thead>
				<tbody>
					{filteredAndSorted.map((request) => {
						const isPending = request.status === "pending";
						const revisionStatus = getRevisionStatus(request.status);
						const actionPerson = request.approved_by ?? request.declined_by;

						return (
							<tr
								key={request.id}
								className={cn(
									"border-b border-gray-100 hover:bg-gray-50",
									isPending && "border-l-4 border-l-amber-400"
								)}
								data-testid={`revision-request-row-${request.id}`}
							>
								<td className="px-4 py-3 font-medium text-gray-900">
									<div className="flex items-center gap-2">
										<CompanyLogoDisplay
											size="sm"
											supplierLogoUrl={request.supplier.logo ?? null}
											supplierName={request.supplier.name}
										/>
										{request.supplier.name}
									</div>
								</td>
								<td className="px-4 py-3 text-gray-600">
									{TYPE_LABELS[request.type] ?? request.type}
								</td>
								<td className="px-4 py-3">
									<span
										className={cn(
											"inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
											APPROVAL_STATUS_STYLES[request.status]
										)}
									>
										{APPROVAL_STATUS_LABELS[request.status]}
									</span>
								</td>
								<td className="px-4 py-3 text-gray-600">
									{request.requested_by?.fullName ?? "—"}
								</td>
								<td className="px-4 py-3 text-gray-600">
									{actionPerson?.fullName ?? "—"}
								</td>
								<td className="px-4 py-3 text-gray-600">
									{revisionStatus ?? "—"}
								</td>
								<td className="px-4 py-3">
									{isPending && (
										<div className="flex items-center gap-1">
											<button
												type="button"
												onClick={() => onApprove(request)}
												disabled={isApproving}
												className={cn(btnBase, "text-green-700 hover:bg-green-50 border border-green-200")}
												data-testid={`approve-btn-${request.id}`}
											>
												<FiCheck size={12} />
												Approve
											</button>
											<button
												type="button"
												onClick={() => onDecline(request)}
												disabled={isApproving}
												className={cn(btnBase, "text-red-700 hover:bg-red-50 border border-red-200")}
												data-testid={`decline-btn-${request.id}`}
											>
												<FiX size={12} />
												Decline
											</button>
										</div>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};
